import { useState } from 'react';
import { apiFetch, API_BASE_URL } from '../../utils/api';
import {
  FaUserShield,
  FaKey,
  FaCheckCircle,
  FaServer,
  FaDatabase,
  FaSave,
  FaSlidersH,
} from 'react-icons/fa';

export default function SettingsPage() {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || 'null');
    } catch {
      return null;
    }
  });

  const [formData, setFormData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    password: '',
    confirmPassword: '',
  });

  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg('');
    setSuccessMsg('');
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!formData.name.trim()) {
      setErrorMsg('Name cannot be empty.');
      return;
    }
    if (!formData.email.trim()) {
      setErrorMsg('Email cannot be empty.');
      return;
    }

    if (formData.password && formData.password.length < 6) {
      setErrorMsg('Password must be at least 6 characters long.');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setErrorMsg('New password and confirmation do not match.');
      return;
    }

    try {
      setSaving(true);
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
      };
      if (formData.password) {
        payload.password = formData.password;
      }

      const res = await apiFetch('/api/users/profile', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to update profile settings.');
      }

      const updatedData = await res.json();
      const updatedUser = {
        ...currentUser,
        ...updatedData,
        token: updatedData.token || currentUser?.token,
      };

      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      setCurrentUser(updatedUser);
      setFormData((prev) => ({
        ...prev,
        password: '',
        confirmPassword: '',
      }));
      setSuccessMsg('Account settings updated successfully!');
    } catch (err) {
      setErrorMsg(err.message || 'An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Page Title */}
      <div>
        <h2 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
          System & Account Settings
        </h2>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">
          Manage your administrator credentials, password, and review platform architecture.
        </p>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold text-emerald-800 shadow-2xs">
          <FaCheckCircle className="text-emerald-500 text-sm" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-xs font-semibold text-red-700 shadow-2xs">
          {errorMsg}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: Account & Password Form (7 Cols) */}
        <div className="lg:col-span-7">
          <form
            onSubmit={handleProfileSubmit}
            className="rounded-3xl border border-slate-200/90 bg-white p-6 sm:p-7 shadow-2xs space-y-6"
          >
            <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <FaUserShield className="text-lg" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Administrator Credentials</h3>
                <p className="text-[11px] text-slate-400">Update your login name and email address</p>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1.5">
                  Display Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Admin User"
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1.5">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. admin@finegal.com"
                  required
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 border-b border-slate-100 pt-3 pb-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                <FaKey className="text-base" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Change Password</h3>
                <p className="text-[11px] text-slate-400">Leave blank if you do not wish to change password</p>
              </div>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1.5">
                  New Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1.5">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter new password"
                  className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-xs text-slate-800 focus:border-[#01295c] focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
                />
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="btn-primary py-2.5 px-6 rounded-xl text-xs font-bold inline-flex items-center gap-2 shadow-sm disabled:opacity-50"
              >
                <FaSave className="text-xs" />
                <span>{saving ? 'Saving...' : 'Save Account Settings'}</span>
              </button>
            </div>
          </form>
        </div>

        {/* Right: Architecture & Status Overview (5 Cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Architecture Card */}
          <div className="rounded-3xl border border-slate-200/90 bg-white p-6 shadow-2xs space-y-4">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <FaServer className="text-emerald-500" />
              <h3 className="text-sm font-bold text-slate-900">Infrastructure Status</h3>
            </div>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                <div>
                  <p className="font-bold text-slate-800 flex items-center gap-1.5">
                    <FaDatabase className="text-emerald-500 text-xs" /> MongoDB Atlas
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Cloud Database Cluster</p>
                </div>
                <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold text-emerald-800">
                  CONNECTED
                </span>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                <div>
                  <p className="font-bold text-slate-800">API Endpoint</p>
                  <p className="text-[11px] text-slate-400 mt-0.5 truncate max-w-[180px]" title={API_BASE_URL}>
                    {API_BASE_URL}
                  </p>
                </div>
                <span className="rounded-md bg-blue-100 px-2 py-0.5 text-[10px] font-extrabold text-blue-800">
                  ONLINE
                </span>
              </div>

              <div className="flex items-center justify-between rounded-xl bg-slate-50 p-3">
                <div>
                  <p className="font-bold text-slate-800">Image Compression</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Sharp WebP Engine</p>
                </div>
                <span className="rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-extrabold text-amber-900">
                  ACTIVE (80%)
                </span>
              </div>
            </div>
          </div>

          {/* Editorial Preferences Info */}
          <div className="rounded-3xl border border-slate-200/90 bg-white p-6 shadow-2xs space-y-3">
            <div className="flex items-center gap-2.5 border-b border-slate-100 pb-3">
              <FaSlidersH className="text-amber-500" />
              <h3 className="text-sm font-bold text-slate-900">Editorial Standards</h3>
            </div>

            <div className="space-y-2.5 text-xs text-slate-600">
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Default Author:</span>
                <span className="font-semibold text-slate-800">Finegal Team</span>
              </div>
              <div className="flex justify-between py-1 border-b border-slate-50">
                <span className="text-slate-500">Default Status:</span>
                <span className="font-semibold text-slate-800">Draft</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-slate-500">Max Title Length:</span>
                <span className="font-semibold text-slate-800">120 characters</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
